import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ideaService } from '../../../services/ideas.service';
import Button from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import Select from '../../UI/Select/Select';
import MultiSelectAutocomplete from '../../UI/MultiSelectAutocomplete/MultiSelectAutocomplete';
import Alert from '../../UI/Alert/Alert';
import styles from '../IdeasCreateForm/IdeasCreateForm.module.css';
import { toast } from 'react-toastify';

function IdeasEditForm({ isOpen, onClose, onSuccess, idea, tagsList = [], statuses = [], locked = false, onLocked }) {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');

    const [title, setTitle] = useState({ value: '', error: null });
    const [description, setDescription] = useState({ value: '', error: null });
    const [tags, setTags] = useState({ value: [], error: null });
    const [status, setStatus] = useState({ value: '', error: null });
    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (idea) {
            setTitle({ value: idea.title || '', error: null });
            setDescription({ value: idea.description || '', error: null });

            // Map tags to the format expected by MultiSelectAutocomplete
            const mappedTags = idea.tags ? idea.tags.map(t => {
                const found = tagsList.find(tagItem => tagItem.code === t || tagItem.value === t);
                return found || t;
            }) : [];

            setTags({ value: mappedTags, error: null });
            setStatus({ value: idea.status || '', error: null });
        }
    }, [idea, tagsList]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setGeneralError(null);

        try {
            const tagsArray = tags.value.map((tag) => tag?.code ?? tag?.value ?? tag);

            const updateData = {
                title: title.value,
                description: description.value,
                tags: tagsArray,
                status: status.value,
            };

            const response = await ideaService.updateIdea(idea.id, updateData);

            if (response.status === 'success' || response.idea || response.id) {
                toast.success(tValidation('idea_edited_successfully'));
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            if (error?.status === 403 && error?.payload?.error === 'editing_is_closed_for_this_event') {
                toast.error(tValidation('editing_is_closed_for_this_event'));
                onLocked?.();
                onClose();
                return;
            }
            const errorEntity = error?.payload?.error;
            if (errorEntity && Array.isArray(errorEntity)) {
                errorEntity.forEach((errorItem) => {
                    const field = errorItem.path?.[0];
                    if (field === 'title') {
                        setTitle(prev => ({ ...prev, error: errorItem }));
                    }
                    if (field === 'description') {
                        setDescription(prev => ({ ...prev, error: errorItem }));
                    }
                    if (field === 'tags') {
                        setTags(prev => ({ ...prev, error: errorItem }));
                    }
                    if (field === 'status') {
                        setStatus(prev => ({ ...prev, error: errorItem }));
                    }
                });
            } else if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity.toString());
                setGeneralError(message);
                toast.error(message);
            } else {
                const message = error.message || 'Failed to update idea';
                setGeneralError(message);
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{t('ideas.edit')}</h2>
                    <p className={styles.subtitle}>{t('ideas.edit_subtitle')}</p>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={isLoading}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {locked && (
                        <Alert type="error">
                            <p>{tValidation('editing_is_closed_for_this_event')}</p>
                        </Alert>
                    )}
                    {generalError && !locked && (
                        <Alert type="error">
                            <p>{generalError}</p>
                        </Alert>
                    )}
                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.title_label')}</label>
                        <Input
                            type="text"
                            placeholder={t('ideas.title_placeholder')}
                            value={title.value}
                            onChange={(e) => {
                                const val = e.target.value;
                                let error = null;
                                if (val.length > 100) {
                                    error = { code: 'title_too_long', params: { maximum: 100 } };
                                }
                                setTitle({ value: val, error });
                            }}
                            error={title.error}
                            required
                            disabled={locked}
                        />
                        <div className={`${styles.charCount} ${title.value.length > 100 ? styles.charCountError : ''}`}>
                            {title.value.length} / 100
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.description_label')}</label>
                        <textarea
                            className={`${styles.textarea} ${description.error ? styles.textareaError : ''}`}
                            placeholder={t('ideas.description_placeholder')}
                            value={description.value}
                            onChange={(e) => {
                                const val = e.target.value;
                                let error = null;
                                if (val.length > 500) {
                                    error = { code: 'description_too_long', params: { maximum: 500 } };
                                }
                                setDescription({ value: val, error });
                            }}
                            rows={4}
                            required
                            disabled={locked}
                        />
                        <div className={`${styles.charCount} ${description.value.length > 500 ? styles.charCountError : ''}`}>
                            {description.value.length} / 500
                        </div>
                        {description.error && (
                            <p className={styles.errorText}>
                                {
                                    tValidation(description.error?.code || '').toString().replace('{{toReplace}}', description.error?.params?.maximum || description.error?.params?.min)
                                }
                            </p>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.tags_label')}</label>
                        <MultiSelectAutocomplete
                            options={tagsList}
                            value={tags.value}
                            onChange={(values) => setTags({ value: values, error: null })}
                            placeholder={t('ideas.tags_placeholder')}
                            error={tags.error}
                            disabled={locked}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.status_label')}</label>
                        <Select
                            options={statuses}
                            value={status.value}
                            onChange={(e) => setStatus({ value: e.target.value, error: null })}
                            placeholder={t('ideas.status')}
                            error={status.error}
                            required
                            disabled={locked}
                        />
                    </div>

                    <div className={styles.buttons}>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('ideas.cancel')}
                        </Button>
                        {!locked && (
                            <Button variant="primary" type="submit" disabled={isLoading || !!description.error || !!title.error}>
                                {t('ideas.save')}
                            </Button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default IdeasEditForm;
